import * as React from 'react';
import {
  Theme,
  makeStyles,
  createStyles,
  Breadcrumbs as BbUiBreadcrumbs,
  BreadcrumbsProps as BbUiBreadcrumbsProps,
  BreadcrumbsItem as BbUiBreadcrumbsItem,
  Link as BbUiLink,
} from '@bb-ui/react-library';
import clsx from 'clsx';
import { matchPath, Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppRouteProps } from 'routes/AppRoute';
import { useAppRouteContext } from 'contexts/AppRouteContext';
import { useTenantContext } from 'contexts/TenantContext';

export interface BreadcrumbsProps extends BbUiBreadcrumbsProps {
  replaceText?: { [key: string]: string };
  className?: string;
}

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      fontSize: theme.typography.fontSizeSmall,
    },
  }),
);

export const Breadcrumbs: React.FunctionComponent<BreadcrumbsProps> = (props) => {
  const { replaceText, className, ...other } = props;
  const { allRoutes } = useAppRouteContext();
  const { tenant, parentTenant } = useTenantContext();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const classes = useStyles(props);

  // Recursively search for the deepest route config matching the current
  // pathname. If there is more than one matching route config at a specific
  // level, we choose the longest path. We need to consider every route config
  // because we will match on a parent route before its children.
  //
  // This is frustratingly similar to what we do in PageTabs but not sure how to
  // generalize the logic.

  function routeForPath(path: string, routes: AppRouteProps[]) {
    let result: AppRouteProps | undefined;
    let resultSpecificity = -1;

    function search(routes: AppRouteProps[], currentSpecificity = 0): void {
      routes.forEach((route) => {
        if (
          matchPath(path, { exact: route.exact, path: route.path }) &&
          (!result ||
            currentSpecificity > resultSpecificity ||
            (currentSpecificity === resultSpecificity && result.path.length < route.path.length))
        ) {
          result = route;
          resultSpecificity = currentSpecificity;
        }

        // Check nested routes.

        if (route.routes) {
          search(route.routes, currentSpecificity + 1);
        }
      });
    }

    search(routes);
    return result;
  }

  function popPath(path: string) {
    return path.replace(/\/[^/]*$/, '');
  }

  let path = pathname;
  let breadcrumbs: { path: string; name: string }[] = [];

  while (path.indexOf('/') !== -1) {
    const route = routeForPath(path, allRoutes);

    if (route && route.name) {
      breadcrumbs.unshift({
        path,
        name: (replaceText && replaceText[route.name]) || t(route.name),
      });
    }

    path = popPath(path);
  }

  const parentId = tenant?.parentTenant;
  const tenantType = tenant?.tenantType;

  if (parentId && tenantType) {
    if (tenantType === 'Learn' && !pathname.includes('/learn-site/')) {
      breadcrumbs.unshift({ path: `/client/${parentId}`, name: `${parentTenant?.name}` });
      breadcrumbs.unshift({ path: `/client`, name: 'Learn Clients' });
      breadcrumbs[2].path = `/client/${parentId}/assets`;
      breadcrumbs[2].name = 'Assets';
    }
  }

  // Special case: if there is only one breadcrumb, show nothing.
  if (breadcrumbs.length < 2) {
    breadcrumbs = [];
  }

  return (
    <BbUiBreadcrumbs
      className={clsx(classes.root, className)}
      data-testid="breadcrumbs"
      {...other}
      itemsBeforeCollapse={breadcrumbs.length}
    >
      {breadcrumbs.map(({ name, path }, index) => (
        <BbUiBreadcrumbsItem
          label={name}
          key={path}
          isCurrentPage={index === breadcrumbs.length - 1}
        >
          <BbUiLink color="inherit" component={RouterLink} to={path}>
            {name}
          </BbUiLink>
        </BbUiBreadcrumbsItem>
      ))}
    </BbUiBreadcrumbs>
  );
};

export default Breadcrumbs;
